// Two big thumb-sized buttons asking "which breast?" — used when the merged
// breast tile is tapped and no feed is in progress.
//
// We don't *prescribe* a side (the "dessert boob" pattern means people often
// start with the side they ended on — opposite of what a naive opposite-of-last
// suggestion would pick). Instead we surface the last feed's summary so the
// user can decide.

import type { FeedingSession } from '@shared/types.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell } from './SheetShell.tsx';
import { summarise, summaryLabel } from '../../lib/breast.ts';
import { fmtTime, relAgo } from '../../lib/format.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (side: 'left' | 'right') => void;
  /** Most recent finished breast feeding session — used to populate the
   *  "Last feed" context line. */
  lastBreast?: FeedingSession | null;
};

export function BreastPickerSheet({ open, onClose, onPick, lastBreast }: Props) {
  const summary = lastBreast ? summarise(lastBreast) : null;
  const sideLabel = summary?.hasHistory ? summaryLabel(summary, { showPaused: false }) : null;
  const endedSide =
    summary && summary.segments.length > 0
      ? summary.segments[summary.segments.length - 1].side
      : null;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title="Which breast?"
      tagline="Tap to start feeding"
      accent="breast"
      icon={(p) => <Icon.breast {...p} />}
      primaryLabel="Cancel"
      secondaryLabel="Close"
      onPrimary={onClose}
    >
      {lastBreast && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 14,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            marginBottom: 12,
            fontSize: 12,
            color: 'var(--text-2)',
          }}
        >
          <Icon.clock s={14} style={{ color: 'var(--text-3)', marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, lineHeight: 1.45 }}>
            <div
              style={{
                color: 'var(--text-3)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.04,
                textTransform: 'uppercase',
              }}
            >
              Last feed
            </div>
            <div style={{ color: 'var(--text-1)', marginTop: 2 }}>
              {sideLabel ?? 'No per-side history'}
              {endedSide && (
                <>
                  {' · ended on '}
                  <span style={{ fontWeight: 600 }}>
                    {endedSide === 'left' ? 'Left' : 'Right'}
                  </span>
                </>
              )}
            </div>
            {lastBreast.ended_at != null && (
              <div style={{ color: 'var(--text-3)', marginTop: 2 }}>
                {relAgo(lastBreast.ended_at)} · ended {fmtTime(lastBreast.ended_at)}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <SideButton
          label="Left"
          onClick={() => {
            onPick('left');
            onClose();
          }}
        />
        <SideButton
          label="Right"
          onClick={() => {
            onPick('right');
            onClose();
          }}
        />
      </div>
    </SheetShell>
  );
}

function SideButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '36px 0 28px',
        borderRadius: 22,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'manipulation',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: 'color-mix(in oklab, var(--breast) 18%, transparent)',
          color: 'var(--breast)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon.breast s={30} />
      </div>
      <div
        className="lt-serif"
        style={{ fontSize: 26, fontWeight: 500, color: 'var(--text-1)', letterSpacing: -0.01 }}
      >
        {label}
      </div>
    </button>
  );
}
